import React, { useState, useCallback } from 'react';
import type { FC } from 'react';
import { match, partial } from 'variant';
import { Box, InputState, SelectGroupsInput } from '@wix/cli-ui-kit';
import type { SelectOption, SelectGroup } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { useWixCliAppBi } from '../../bi/index.js';
import { useGenerateDashboardPage } from './dashboad-page/index.js';
import { useGenerateEmbeddedScript } from './embedded-script/index.js';
import { useGenerateSidebarCategory } from './sidebar-category/index.js';
import { useGenerateDashboardModal } from './dashboard-modal/DashboardModal.js';
import { useGenerateServicePlugin } from './service-plugin/index.js';
import { DashboardPlugin } from './plugins/DashboardPlugin.js';
import { GenerateSitePlugin } from './site-plugin/index.js';
import { useGenerateApi } from './api/index.js';
import { useGenerateEvent } from './event/index.js';
import { useGenerateSiteWidget } from './site-widget/index.js';
import { Wizard } from './Wizard.js';
import { useGenerateFlows } from './generateFlows.js';
import { getGenerateFlowByDocsExtensionType } from './docsExtensionType.js';
import type { GenerateFlow } from './generateFlows.js';
import type { DocsExtensionType } from './docsExtensionType.js';

export const GenerateCommand: FC<{ docsExtensionType?: DocsExtensionType }> = ({
  docsExtensionType,
}) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();

  const dashboardPageSteps = useGenerateDashboardPage();
  const dashboardModalSteps = useGenerateDashboardModal();
  const apiSteps = useGenerateApi(docsExtensionType);
  const generateEventsSteps = useGenerateEvent();
  const generateServicePluginSteps = useGenerateServicePlugin();
  const generateEmbeddedScriptSteps = useGenerateEmbeddedScript();
  const generateSidebarCategorySteps = useGenerateSidebarCategory();
  const generateSiteWidgetSteps = useGenerateSiteWidget();

  const biCliFlowStepAnswered = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      bi.cliFlowStepAnswered({
        question: t('generate_command.flow.choose_flow'),
        questionKey: 'generate_command.flow.choose_flow',
        answer: selectedType.title,
      });
    },
    [bi, t]
  );

  const [generateFlow, setGenerateFlow] = useState<
    SelectOption<GenerateFlow> | undefined
  >(() => {
    if (docsExtensionType) {
      const selectedType = getGenerateFlowByDocsExtensionType(
        docsExtensionType,
        t
      );
      biCliFlowStepAnswered(selectedType);
      return selectedType;
    }
    return;
  });

  const generateFlows: Array<SelectGroup<GenerateFlow>> = useGenerateFlows();

  const selectFlow = useCallback(
    (selectedType: SelectOption<GenerateFlow>) => {
      biCliFlowStepAnswered(selectedType);
      setGenerateFlow(selectedType);
    },
    [biCliFlowStepAnswered]
  );

  const onExitWizard = () => {
    setGenerateFlow(undefined);
  };

  return (
    <Box flexDirection="column" paddingTop={1}>
      <SelectGroupsInput<GenerateFlow>
        value={
          generateFlow
            ? InputState.Submitted(generateFlow)
            : InputState.NotSubmitted(generateFlow)
        }
        label={t('generate_command.flow.choose_flow')}
        groups={generateFlows}
        onSubmit={selectFlow}
        maxHeight={15}
      />
      {generateFlow &&
        match(
          generateFlow.value,
          partial({
            DashboardPage: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={dashboardPageSteps}
              />
            ),
            DashboardModal: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={dashboardModalSteps}
              />
            ),
            Api: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={apiSteps}
              />
            ),
            DashboardSidebarCategory: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={generateSidebarCategorySteps}
              />
            ),
            EmbeddedScript: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={generateEmbeddedScriptSteps}
              />
            ),
            CustomElement: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={generateSiteWidgetSteps}
              />
            ),
            ServicePlugin: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={generateServicePluginSteps}
              />
            ),
            Event: () => (
              <Wizard
                name={generateFlow.value.type}
                onExitWizard={onExitWizard}
                steps={generateEventsSteps}
              />
            ),
            SitePlugin: () => <GenerateSitePlugin />,
            DashboardPlugin: () => (
              <DashboardPlugin flow={generateFlow.value} />
            ),
            DashboardMenuPlugin: () => (
              <DashboardPlugin flow={generateFlow.value} />
            ),
            default: () => null,
          })
        )}
    </Box>
  );
};
